/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Anchor, Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';

import { useAIAnswerContext } from '../../../../ai-answer-dialog/ai-answer-context';
import { onAIAnswerSourceClick } from '../../../../ai-answer-dialog/analytics';
import { AppIcon } from '../../../../common/ui/app-icon/app-icon';
import { type ObjectResolverBody } from '../../../common/hooks/use-fetch-smartlink-data';

const toTitleCase = (sourceCategory: string) =>
	sourceCategory
		.split(' ')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');

interface DefinitionSourceProps {
	sourceData: ObjectResolverBody;
	index: number;
}

const styles = cssMap({
	root: {
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'none',
		},
	},
	sourceIconWrapperStyles: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		width: '16px',
		height: '16px',
	},
});

export const DefinitionSource: ({ sourceData, index }: DefinitionSourceProps) => JSX.Element = ({
	sourceData,
	index,
}: DefinitionSourceProps) => {
	const { commonAttributes, fireAnalyticsEvent } = useAIAnswerContext();

	const is1PSource = sourceData.data.generator?.name === 'Confluence';
	const sourceMetadata = is1PSource
		? sourceData.data.summary
		: sourceData.entityData?.type
			? toTitleCase(sourceData.entityData.type.category)
			: undefined;

	const onClickSource = useCallback(() => {
		fireAnalyticsEvent(
			onAIAnswerSourceClick({
				sourceIndex: index,
				sourceProduct: sourceData.data.generator?.name.toLowerCase() || '',
				...commonAttributes,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [fireAnalyticsEvent, commonAttributes]);

	return (
		<Anchor href={sourceData.data.url} xcss={styles.root} target="_blank" onClick={onClickSource}>
			<Stack space="space.100">
				<Inline alignBlock="center" space="space.100">
					{sourceData.data.generator && (
						<AppIcon app={sourceData.data.generator.name.toLowerCase()} size="xxsmall" />
					)}
					<Box>
						<Text color="color.text" maxLines={1} weight="medium">
							{sourceData.data.name}
						</Text>
					</Box>
				</Inline>
				<Box>
					<Text maxLines={2} color="color.text.subtle" size="small">
						{sourceMetadata}
					</Text>
				</Box>
			</Stack>
		</Anchor>
	);
};
